import { getLocale, setLocale, LANGUAGE_NAMES, LOCALES, tx } from "@/lib/i18n";
import { useLocale } from "@/lib/i18n-react";
import { SelectField } from "./ui/select";
import { useEffect, useState, type ReactNode } from "react";
import {
  BarChart3,
  Building2,
  ChevronRight,
  ChevronsUpDown,
  Code2,
  Files,
  Languages,
  LayoutDashboard,
  ListChecks,
  MapPinned,
  PackageSearch,
  PanelLeft,
  Phone,
  Plug,
  Search,
  Settings2,
  ShieldCheck,
  Sun,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { cn } from "@/lib/utils";
import type { PageId } from "@/lib/types";

const NAVIGATION = [
  { id: "dashboards" as const, label: "Dashboards", icon: LayoutDashboard },
  { id: "widgets" as const, label: "Widgets", icon: BarChart3 },
  { id: "bindings" as const, label: "Data bindings", icon: Plug },
  { id: "templates" as const, label: "Report Templates", icon: Code2 },
  { id: "reports" as const, label: "Reports", icon: Files },
];
// Sidebar and sticky header extracted from the shift-management prototype shell.
const navClass =
  "flex items-center w-full min-h-9 rounded-md text-left text-sm font-medium text-[#626268] py-2 px-2.5 gap-2.5 [&_svg]:shrink-0 [&_svg]:size-[17px] [&[aria-current=page]]:bg-[#f0f0f2] [&[aria-current=page]]:text-[#27272a] [&[aria-current=page]]:font-semibold [&:not(:disabled):hover]:bg-[#ededf0]";
export type BreadcrumbItem = { label: string; page?: PageId };
export function AppShell({
  page,
  breadcrumbs,
  onNavigate,
  onReset,
  children,
}: {
  page: PageId;
  breadcrumbs: BreadcrumbItem[];
  onNavigate: (page: PageId) => void;
  onReset: () => void;
  children: ReactNode;
}) {
  useLocale();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [search, setSearch] = useState("");
  const [settings, setSettings] = useState(false);
  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSidebarOpen(false);
        setSearch("");
      }
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);
  const navigate = (id: PageId) => {
    onNavigate(id);
    setSearch("");
    setSidebarOpen(false);
  };
  const matches = NAVIGATION.filter((item) =>
    tx(item.label).toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <div className="min-h-screen bg-background text-foreground">
      {sidebarOpen && (
        <button
          className="fixed inset-0 z-30 bg-slate-950/30 lg:hidden"
          aria-label={tx("Close navigation")}
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside
        id="main-navigation-sidebar"
        className={cn(
          "fixed inset-y-0 start-0 z-40 flex w-64 flex-col border-e border-border bg-[#fafafa] p-2 transition-transform",
          sidebarOpen ? "visible translate-x-0" : "invisible -translate-x-full",
          desktopOpen
            ? "lg:visible lg:translate-x-0"
            : "lg:invisible lg:-translate-x-full",
        )}
      >
        <div className="flex h-[76px] shrink-0 items-center gap-2.5 px-2">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-white">
            <Building2 className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-zinc-700">
              {"NClean · Katinkulta"}
            </p>
            <p className="text-xs text-muted-foreground">
              {tx("Management workspace")}
            </p>
          </div>
          <ChevronsUpDown className="size-4 text-muted-foreground" />
        </div>
        <nav
          className="flex-1 overflow-y-auto"
          aria-label={tx("Main navigation")}
        >
          <p className="mx-2 mb-2.5 mt-7 text-xs font-semibold uppercase tracking-[.035em] text-[#7b7b80]">
            {tx("Operations & dispatch")}
          </p>
          <div className="flex items-center gap-2 px-2.5 pb-2 text-xs font-semibold text-zinc-700">
            <BarChart3 className="size-[17px]" />
            {tx("Analytics")}
            <span className="ms-auto rounded bg-teal-100 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-teal-800">
              {tx("Preview")}
            </span>
          </div>
          {NAVIGATION.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={cn(navClass, "ps-3")}
              aria-current={
                page === id ||
                ((page === "data-model" || page === "builder") &&
                  id === "bindings")
                  ? "page"
                  : undefined
              }
              onClick={() => navigate(id)}
            >
              <Icon />
              {tx(label)}
            </button>
          ))}
          <p className="mx-2 mb-2.5 mt-7 text-xs font-semibold uppercase tracking-[.035em] text-[#7b7b80]">
            {tx("My work")}
          </p>
          {[
            { label: tx("Profile & performance"), icon: BarChart3 },
            { label: tx("Cleaning guidance"), icon: ListChecks },
            { label: tx("Support number"), icon: Phone },
          ].map(({ label, icon: Icon }) => (
            <button
              key={tx(label)}
              disabled
              title={tx("Available in the full Luuta workspace")}
              className={navClass}
            >
              <Icon />
              {tx(label)}
            </button>
          ))}
          <p className="mx-2 mb-2.5 mt-7 text-xs font-semibold uppercase tracking-[.035em] text-[#7b7b80]">
            {tx("Administration")}
          </p>
          {[
            { label: tx("Organization"), icon: ShieldCheck },
            { label: tx("Locations"), icon: MapPinned },
            { label: tx("Work rules"), icon: ListChecks },
            { label: tx("Supplies"), icon: PackageSearch },
            { label: tx("Integrations"), icon: Plug },
          ].map(({ label, icon: Icon }) => (
            <button
              key={tx(label)}
              disabled
              title={tx("Available in the full Luuta workspace")}
              className={navClass}
            >
              <Icon />
              {tx(label)}
              <ChevronRight className="ms-auto" />
            </button>
          ))}
        </nav>
        <button
          className={cn(navClass, "mb-2 mt-2")}
          onClick={() => setSettings(true)}
        >
          <Settings2 />
          {tx("Workspace settings")}
        </button>
        <div className="flex shrink-0 items-center gap-2.5 border-t border-border px-2 py-4">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-orange-500 text-xs font-semibold text-white">
            {tx("LA")}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-zinc-700">
              {tx("Luuta Operations Manager")}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {`NClean · ${tx("Management workspace")}`}
            </p>
          </div>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </div>
      </aside>
      <div
        className={cn("min-w-0 transition-[margin]", desktopOpen && "lg:ms-64")}
      >
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-4 border-b border-border bg-white px-4 lg:px-8">
          <div className="flex min-w-0 flex-1 items-center gap-4 lg:gap-8">
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 lg:hidden"
              aria-label={tx("Open navigation")}
              aria-controls="main-navigation-sidebar"
              aria-expanded={sidebarOpen}
              onClick={() => setSidebarOpen(true)}
            >
              <PanelLeft />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="hidden shrink-0 lg:inline-flex"
              aria-label={
                desktopOpen ? tx("Close navigation") : tx("Open navigation")
              }
              aria-expanded={desktopOpen}
              aria-controls="main-navigation-sidebar"
              onClick={() => setDesktopOpen(!desktopOpen)}
            >
              <PanelLeft />
            </Button>
            <nav aria-label={tx("Breadcrumb")} className="min-w-0 text-sm">
              <ol className="flex min-w-0 items-center gap-2">
                {breadcrumbs.map((item, index) => {
                  const destination = item.page;
                  return (
                    <li
                      key={index}
                      className={cn(
                        "flex items-center gap-2",
                        destination ? "shrink-0" : "min-w-0",
                        index === 0 && "hidden md:flex",
                        page === "data-model" &&
                          index < breadcrumbs.length - 1 &&
                          "hidden sm:flex",
                      )}
                    >
                      {destination ? (
                        <>
                          <a
                            href={`#/${destination}`}
                            onClick={(event) => {
                              if (
                                event.button !== 0 ||
                                event.metaKey ||
                                event.ctrlKey ||
                                event.shiftKey ||
                                event.altKey
                              )
                                return;
                              event.preventDefault();
                              navigate(destination);
                            }}
                            className="rounded-sm whitespace-nowrap font-medium text-muted-foreground hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {item.label}
                          </a>
                          <span className="text-slate-300" aria-hidden="true">
                            /
                          </span>
                        </>
                      ) : (
                        <span
                          className="truncate font-semibold"
                          aria-current="page"
                          title={item.label}
                        >
                          {item.label}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <div className="relative hidden xl:block">
              <Search className="pointer-events-none absolute start-3 top-3 size-4 text-muted-foreground" />
              <input
                aria-label={tx("Search features")}
                placeholder={tx("Search features…")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && matches[0]) navigate(matches[0].id);
                }}
                className="h-10 w-56 rounded-md border border-border bg-white pe-3 ps-9 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
              {search && (
                <div className="absolute end-0 top-12 w-64 rounded-lg border border-border bg-white p-2 shadow-lg">
                  {matches.length ? (
                    matches.map((item) => (
                      <button
                        key={item.id}
                        className={navClass}
                        onClick={() => navigate(item.id)}
                      >
                        {tx(item.label)}
                      </button>
                    ))
                  ) : (
                    <p className="p-2 text-sm text-muted-foreground">
                      {tx("No matching features")}
                    </p>
                  )}
                </div>
              )}
            </div>
            <label className="flex h-10 items-center gap-2 rounded-md border border-border px-3">
              <Languages className="size-4" />
              <SelectField
                aria-label={tx("Interface language")}
                value={getLocale()}
                onValueChange={(value) => {
                  if (LOCALES.includes(value as (typeof LOCALES)[number]))
                    setLocale(value as (typeof LOCALES)[number]);
                }}
                className="h-8 w-auto border-0 bg-transparent px-0 text-sm font-medium shadow-none"
              >
                {LOCALES.map((locale) => (
                  <option key={locale} value={locale}>
                    {LANGUAGE_NAMES[locale]}
                  </option>
                ))}
              </SelectField>
            </label>
            <span className="hidden h-10 items-center gap-2 rounded-md border border-border px-3 text-sm font-medium sm:flex">
              <Sun className="size-4" />
              {tx("Light")}
            </span>
          </div>
        </header>
        <main
          id="main-content"
          tabIndex={-1}
          className={cn(
            "mx-auto max-w-[1760px]",
            page === "builder" || page === "data-model"
              ? "flex h-[calc(100dvh-64px)] min-h-0 flex-col overflow-hidden p-2 lg:p-3"
              : "px-4 py-6 sm:px-6 lg:px-8 lg:py-7",
          )}
        >
          {children}
        </main>
      </div>
      <Dialog open={settings} onOpenChange={setSettings}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tx("Workspace settings")}</DialogTitle>
            <DialogDescription>
              {tx("Manage your Luuta Analytics workspace.")}
            </DialogDescription>
          </DialogHeader>
          <dl className="space-y-4 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{tx("Operating Unit")}</dt>
              <dd className="font-medium">{"NClean · Katinkulta"}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{tx("Data snapshot")}</dt>
              <dd className="font-medium">
                {tx("Source dates shown in each view")}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">
                {tx("Available history")}
              </dt>
              <dd className="font-medium">
                {tx("May and October 2026 source examples")}
              </dd>
            </div>
          </dl>
          <div className="mt-6 rounded-lg bg-muted p-4 text-sm leading-6 text-muted-foreground">
            {tx("The metric definitions can be reviewed from the dashboard.")}
          </div>
          <Button
            className="mt-6 w-full"
            variant="outline"
            onClick={() => {
              onReset();
              setSettings(false);
            }}
          >
            {tx("Restore default reports")}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
